/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useState, useEffect } from 'react'

/**
 * ScrollSync provider component
 */
interface ScrollSyncOptions {
	enabled?: boolean
	onSync?: (el: Element) => void
	proportional?: boolean
	vertical?: boolean
	horizontal?: boolean
}

interface UseScrollSyncReturn {
	registerPane: (node: HTMLElement) => void
	unregisterPane: (node: HTMLElement) => void
}

interface useScrollSyncProps {
	/**
	 * Callback to be invoked any time synchronization happens
	 *
	 * @param {Element} el The element that has received the scroll event
	 */
	onSync?(...args: unknown[]): unknown
	proportional?: boolean
	vertical?: boolean
	horizontal?: boolean
	enabled?: boolean
}

const useScrollSync = ({
	enabled = true,
	onSync = () => {},
	proportional = true,
	vertical = true,
	horizontal = true,
}: useScrollSyncProps): UseScrollSyncReturn => {
	const [panes, setPanes] = useState<HTMLElement[]>([])

	const findPane = useCallback(
		(node: HTMLElement): HTMLElement | undefined => {
			return panes.find((pane) => pane === node)
		},
		[panes],
	)

	interface SyncScrollPositionParams {
		scrolledPane: HTMLElement
		pane: HTMLElement
	}

	const syncScrollPosition = useCallback(
		({ scrolledPane, pane }: SyncScrollPositionParams) => {
			const {
				scrollTop,
				scrollHeight,
				clientHeight,
				scrollLeft,
				scrollWidth,
				clientWidth,
			} = scrolledPane

			const scrollTopOffset = scrollHeight - clientHeight
			const scrollLeftOffset = scrollWidth - clientWidth

			/* Calculate the actual pane height */
			const paneHeight = pane.scrollHeight - clientHeight
			const paneWidth = pane.scrollWidth - clientWidth
			/* Adjust the scrollTop position of it accordingly */
			if (vertical && scrollTopOffset > 0) {
				pane.scrollTop = proportional
					? (paneHeight * scrollTop) / scrollTopOffset
					: scrollTop
			}
			if (horizontal && scrollLeftOffset > 0) {
				pane.scrollLeft = proportional
					? (paneWidth * scrollLeft) / scrollLeftOffset
					: scrollLeft
			}
		},
		[vertical, horizontal],
	)

	const removeEvents = useCallback((node: HTMLElement) => {
		/* For some reason element.removeEventListener doesnt work with document.body */
		node.onscroll = null
	}, [])

	interface SyncScrollPositionsParams {
		scrolledPane: HTMLElement
		_panes: HTMLElement[]
	}

	const syncScrollPositions = useCallback(
		({ scrolledPane, _panes }: SyncScrollPositionsParams) => {
			_panes.forEach((paneNodeRef) => {
				/* For all panes beside the currently scrolling one */
				if (scrolledPane !== paneNodeRef) {
					/* Remove event listeners from the node that we'll manipulate */
					removeEvents(paneNodeRef)
					syncScrollPosition({ scrolledPane, pane: paneNodeRef })
					/* Re-attach event listeners after we're done scrolling */
					window.requestAnimationFrame(() => {
						addEvents(paneNodeRef, _panes)
					})
				}
			})
			if (onSync) onSync(scrolledPane)
		},
		[removeEvents, syncScrollPosition, onSync],
	)

	const handlePaneScroll = useCallback(
		(node: HTMLElement, _panes: HTMLElement[]) => {
			if (!enabled) {
				return
			}
			window.requestAnimationFrame(() => {
				syncScrollPositions({ scrolledPane: node, _panes })
			})
		},
		[syncScrollPosition],
	)

	const addEvents = useCallback(
		(node: HTMLElement, _panes: HTMLElement[]) => {
			/* For some reason element.addEventListener doesnt work with document.body */
			node.onscroll = handlePaneScroll.bind(this, node, _panes)
		},
		[handlePaneScroll],
	)

	const registerPane = useCallback(
		(node: HTMLElement) => {
			if (!findPane(node)) {
				if (panes[0]) {
					syncScrollPosition({ scrolledPane: panes[0], pane: node })
				}
				setPanes((prev) => [...prev, node])
			}
		},
		[panes, findPane, addEvents],
	)

	const unregisterPane = useCallback(
		(node: HTMLElement) => {
			if (findPane(node)) {
				removeEvents(node)
				setPanes((prev) => prev.splice(prev.indexOf(node), 1))
			}
		},
		[findPane, removeEvents],
	)

	useEffect(() => {
		// Update events when registering more panes
		panes.forEach((pane) => {
			addEvents(pane, panes)
		})
	}, [panes])

	return {
		registerPane,
		unregisterPane,
	}
}

export default useScrollSync

export const useScrollSyncWrap = ({
	nodeRefs,
	options = {},
}: {
	nodeRefs: React.RefObject<HTMLElement>[]
	options?: ScrollSyncOptions
}) => {
	const { registerPane, unregisterPane } = useScrollSync(options)

	useEffect(() => {
		nodeRefs.forEach((nodeRef) => {
			if (nodeRef && nodeRef.current) {
				registerPane(nodeRef.current)
			}
		})
		return () =>
			nodeRefs.forEach((nodeRef) => {
				if (nodeRef && nodeRef.current) {
					unregisterPane(nodeRef.current)
				}
			})
	}, [nodeRefs, registerPane, unregisterPane])

	return {}
}
